import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'

import EditIcon from '@mui/icons-material/Edit'

import { activitySchema } from '../../schemas/activitySchema'
import { BoldFormTitle } from '../styles/Text'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton, SecondaryIconButton } from '../common/Button'
import axios from '../../dataClient/dataClient'
import ActivityForm from './ActivityForm'
import { constructDateFromDateAndTime } from '../../util/formatting'

function EditActivityButton({ activity, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const defaultValues = useMemo(
    () => ({
      label: activity.name,
      date: format(activity.date, 'MMM-dd-yyyy'),
      time: activity.date
    }),
    [activity]
  )

  const methods = useForm({ resolver: yupResolver(activitySchema), defaultValues })
  const { handleSubmit, reset } = methods

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const handleOpen = () => {
    setError(false)
    reset(defaultValues)
    setIsVisible(true)
  }

  const handleClose = () => {
    setIsVisible(false)
    reset(defaultValues)
    setError(false)
    setIsLoading(false)
  }

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const activityDate = constructDateFromDateAndTime(data.date, data.time)

    axios
      .patch(`/activity/${activity.id}`, {
        date: activityDate,
        name: data.label
      })
      .then(({ data: responseData }) => {
        const updatedData = responseData?.data ?? {}
        const updatedActivity = {
          ...activity,
          ...updatedData,
          name: updatedData.name ?? data.label,
          date: updatedData.date ? new Date(updatedData.date) : activityDate
        }
        handleClose()
        onSuccess(updatedActivity)
      })
      .catch(e => {
        console.error('Failed updating activity', e)
        setError(true)
        setIsLoading(false)
      })
  }

  return (
    <>
      <SecondaryIconButton aria-label="Edit Activity" onClick={handleOpen}>
        <EditIcon />
      </SecondaryIconButton>
      <StyledDialog fullWidth open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <BoldFormTitle>Edit Activity</BoldFormTitle>
              <ActivityForm />
              {error && <ErrorMessage>There was an error saving the activity. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  Save
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

EditActivityButton.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['NEW_REQUEST', 'STATUS_CHANGE', 'MANUAL']).isRequired,
    name: PropTypes.string.isRequired,
    date: PropTypes.instanceOf(Date).isRequired
  }).isRequired,
  onSuccess: PropTypes.func
}

export default EditActivityButton
